// SEO Page Data for Geis Care Mallorca
// Modular architecture: pages split into separate files for maintainability

export interface SeoPageData {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  h1: string;
  intro: string;
  services: { icon: string; title: string; desc: string }[];
  serviceAreaText: string;
  faq: { q: string; a: string }[];
  internalLinks: { label: string; slug: string }[];
  externalLinks?: { label: string; url: string; desc: string }[];
  breadcrumbs: { label: string; href: string }[];
  type: 'service' | 'location' | 'info' | 'guide' | 'ratgeber';
  contentSections?: {
    eyebrow?: string;
    title: string;
    paragraphs?: string[];
    bullets?: string[];
    note?: string;
  }[];
  servicesLabel?: string;
  servicesTitle?: string;
  contactTitle?: string;
  contactText?: string;
}

import { servicePages } from './pages/servicePages';
import { locationPages } from './pages/locationPages';
import { guidePages } from './pages/guidePages';
import { ratgeberPages } from './pages/ratgeberPages';

// Add prefixes to guide and ratgeber slugs
const prefixedGuidePages: Record<string, SeoPageData> = {};
Object.entries(guidePages).forEach(([slug, page]) => {
  prefixedGuidePages[`guide/${slug}`] = page;
});

const prefixedRatgeberPages: Record<string, SeoPageData> = {};
Object.entries(ratgeberPages).forEach(([slug, page]) => {
  prefixedRatgeberPages[`ratgeber/${slug}`] = page;
});

export const seoPages: Record<string, SeoPageData> = {
  ...servicePages,
  ...locationPages,
  ...prefixedGuidePages,
  ...prefixedRatgeberPages,
};

export const allSeoSlugs = Object.keys(seoPages);

// Footer link arrays with multilingual labels
export const footerServiceLinks: { slug: string; labels: { de: string; en: string; es: string } }[] = [
  { slug: 'pflege-mallorca', labels: { de: 'Pflege Mallorca', en: 'Care Mallorca', es: 'Cuidado Mallorca' } },
  { slug: 'intensivpflege-mallorca', labels: { de: 'Intensivpflege', en: 'Intensive Care', es: 'Cuidados Intensivos' } },
  { slug: 'seniorenbetreuung-mallorca', labels: { de: 'Seniorenbetreuung', en: 'Senior Care', es: 'Atencion a Mayores' } },
  { slug: 'hauspflege-mallorca', labels: { de: 'Hauspflege', en: 'Home Care', es: 'Cuidado a Domicilio' } },
  { slug: 'demenzbetreuung-mallorca', labels: { de: 'Demenzbetreuung', en: 'Dementia Care', es: 'Cuidado Demencia' } },
  { slug: 'aerzte-mallorca', labels: { de: 'Aerzte Mallorca', en: 'Doctors Mallorca', es: 'Medicos Mallorca' } },
  { slug: 'kliniken-mallorca', labels: { de: 'Kliniken Mallorca', en: 'Clinics Mallorca', es: 'Clinicas Mallorca' } },
  { slug: 'notfallnummern-mallorca', labels: { de: 'Notfallnummern', en: 'Emergency Numbers', es: 'Numeros de Emergencia' } },
  { slug: 'gesundheitssystem-mallorca', labels: { de: 'Gesundheitssystem', en: 'Healthcare System', es: 'Sistema Sanitario' } },
];

export const footerLocationLinks: { slug: string; labels: { de: string; en: string; es: string } }[] = [
  { slug: 'pflege-palma', labels: { de: 'Pflege Palma', en: 'Care Palma', es: 'Cuidado Palma' } },
  { slug: 'pflege-manacor', labels: { de: 'Pflege Manacor', en: 'Care Manacor', es: 'Cuidado Manacor' } },
  { slug: 'pflege-sant-llorenc', labels: { de: 'Pflege Sant Llorenc', en: 'Care Sant Llorenc', es: 'Cuidado Sant Llorenc' } },
  { slug: 'pflege-cala-millor', labels: { de: 'Pflege Cala Millor', en: 'Care Cala Millor', es: 'Cuidado Cala Millor' } },
  { slug: 'pflege-inca', labels: { de: 'Pflege Inca', en: 'Care Inca', es: 'Cuidado Inca' } },
  { slug: 'pflege-andratx', labels: { de: 'Pflege Andratx', en: 'Care Andratx', es: 'Cuidado Andratx' } },
  { slug: 'pflege-alcudia', labels: { de: 'Pflege Alcudia', en: 'Care Alcudia', es: 'Cuidado Alcudia' } },
  { slug: 'pflege-santa-ponsa', labels: { de: 'Pflege Santa Ponsa', en: 'Care Santa Ponsa', es: 'Cuidado Santa Ponsa' } },
  { slug: 'pflege-porto-cristo', labels: { de: 'Pflege Porto Cristo', en: 'Care Porto Cristo', es: 'Cuidado Porto Cristo' } },
  { slug: 'pflege-campos', labels: { de: 'Pflege Campos', en: 'Care Campos', es: 'Cuidado Campos' } },
  { slug: 'pflege-soller', labels: { de: 'Pflege Soller', en: 'Care Soller', es: 'Cuidado Soller' } },
  { slug: 'pflege-pollenca', labels: { de: 'Pflege Pollenca', en: 'Care Pollenca', es: 'Cuidado Pollenca' } },
  { slug: 'pflege-calvia', labels: { de: 'Pflege Calvia', en: 'Care Calvia', es: 'Cuidado Calvia' } },
];

export const footerGuideLinks: { slug: string; labels: { de: string; en: string; es: string } }[] = [
  { slug: 'golfplaetze-mallorca', labels: { de: 'Golfplaetze Mallorca', en: 'Golf Courses Mallorca', es: 'Campos de Golf Mallorca' } },
  { slug: 'mandelbluete-mallorca', labels: { de: 'Mandelbluete Mallorca', en: 'Almond Blossom Mallorca', es: 'Almendros en Flor Mallorca' } },
  { slug: 'ostern-mallorca', labels: { de: 'Ostern Mallorca', en: 'Easter Mallorca', es: 'Semana Santa Mallorca' } },
  { slug: 'silvester-mallorca', labels: { de: 'Silvester Mallorca', en: 'New Year Mallorca', es: 'Nochevieja Mallorca' } },
  { slug: 'sant-antoni-mallorca', labels: { de: 'Sant Antoni Mallorca', en: 'Sant Antoni Mallorca', es: 'Sant Antoni Mallorca' } },
  { slug: 'karneval-mallorca', labels: { de: 'Karneval Mallorca', en: 'Carnival Mallorca', es: 'Carnaval Mallorca' } },
  { slug: 'weihnachten-mallorca', labels: { de: 'Weihnachten Mallorca', en: 'Christmas Mallorca', es: 'Navidad Mallorca' } },
  { slug: 'weihnachtsmaerkte-mallorca', labels: { de: 'Weihnachtsmaerkte', en: 'Christmas Markets', es: 'Mercados Navidad' } },
];

export const footerRatgeberLinks: { slug: string; labels: { de: string; en: string; es: string } }[] = [
  { slug: 'pflegegrad-mallorca', labels: { de: 'Pflegegrad beantragen', en: 'Apply for Care Level', es: 'Solicitar Grado de Cuidado' } },
  { slug: 'arzttermin-mallorca', labels: { de: 'Arzttermin Mallorca', en: 'Doctor Appointment', es: 'Cita Medica Mallorca' } },
  { slug: 'medikamente-mallorca', labels: { de: 'Medikamente Mallorca', en: 'Medication Mallorca', es: 'Medicamentos Mallorca' } },
  { slug: 'krankenversicherung-mallorca', labels: { de: 'Krankenversicherung', en: 'Health Insurance', es: 'Seguro Medico' } },
  { slug: 'seniorenhilfe-mallorca', labels: { de: 'Seniorenhilfe Ablauf', en: 'Senior Help Process', es: 'Proceso Ayuda Mayores' } },
  { slug: 'notfall-mallorca', labels: { de: 'Notfall Mallorca', en: 'Emergency Mallorca', es: 'Emergencia Mallorca' } },
  { slug: 'krankenhausaufnahme-mallorca', labels: { de: 'Krankenhausaufnahme', en: 'Hospital Admission', es: 'Ingreso Hospitalario' } },
];
